import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
  Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { formatText } from '../../common/TextFormatterProvider';
import AppDatePicker from '../../common/AppDatePicker';
import SmartUploadFiles from './SmartUploadFiles';
import FileReviewDialog from './FileReviewDialog';
import { FileStatusIcon, fileStatusLabel, LANDING_ATTENTION_MESSAGE, ledgerSummaryLine } from './ingestionStatusDisplay';
import { collection, query, where, orderBy, limit, onSnapshot, getDocsFromServer, startAfter } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { bucketOf, FILE_LABEL } from '../../../utils/ingestionStatus';
import { buildProfileNameById, isDeletedProfile, profileName } from '../../../utils/profileDisplay';
import { format } from 'date-fns';
import { formatTimestamp, formatDateOnly } from '../../../utils/timestamps';

function safeToDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

// Every file status, and the ones each summary card stands for, counted as the
// profile counters count them (Spec 6 section 5): Processing includes the files
// being deleted.
const ALL_STATUSES = Object.keys(FILE_LABEL);
const STATUS_GROUPS = {
  completed: ALL_STATUSES.filter((status) => bucketOf(status) === 'completed'),
  processing: ALL_STATUSES.filter((status) => ['processing', 'deleting'].includes(bucketOf(status))),
  needsReview: ALL_STATUSES.filter((status) => bucketOf(status) === 'attention'),
};

const PAGE_SIZE = 100;             // docs per server read in scan mode
const COMPLETE_FETCH_CAP = 500;    // max docs the complete path may hold
const SCAN_CHUNKS_PER_FILL = 5;    // max server reads per fill (initial load / Search older files)
const FILL_TARGET = 25;            // stop a fill early once this many new rows match

// A date-only filter value is the picker's YYYY-MM-DD string. `new Date(string)` would
// read it as UTC midnight — 01:00 local in British Summer Time, dropping the first hour
// of the day — so it is built from its parts, which JavaScript reads as local time.
function parseLocalDate(text) {
  const [year, month, day] = String(text).split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Every active filter, applied client-side. A filter that also ran server-side
// re-applies as a no-op; this keeps the predicate independent of which filter
// won the server slot. The date range is the file's reporting date, its `date`:
// an ISO YYYY-MM-DD text, as the picker's values are, so the two compare as text.
function matchesFilters(file, filters) {
  if (filters.profileId && file.data_profile_id !== filters.profileId) return false;
  if (filters.hasWarnings && !file.has_warnings) return false;
  if (filters.statuses.length > 0 && !filters.statuses.includes(file.status)) return false;
  if (filters.dateFrom || filters.dateTo) {
    if (!file.date) return false;
    if (filters.dateFrom && file.date < filters.dateFrom) return false;
    if (filters.dateTo && file.date > filters.dateTo) return false;
  }
  return true;
}

// A status as the status filter lists it: the word the files list uses, with the
// status itself when two statuses share a word ('Queued' is both an approved file
// and one waiting for the next check).
function statusOptionLabel(status) {
  const label = fileStatusLabel(status);
  const shared = ALL_STATUSES.some((other) => other !== status && fileStatusLabel(other) === label);
  return shared ? `${label} (${formatText(status)})` : label;
}

// Newest reporting date first (applied to the loaded set only), then newest upload.
function compareFiles(a, b) {
  if (a.date !== b.date) {
    if (!a.date) return 1;
    if (!b.date) return -1;
    return a.date < b.date ? 1 : -1;
  }
  const ad = safeToDate(a.created_at) || new Date(0);
  const bd = safeToDate(b.created_at) || new Date(0);
  return bd - ad;
}

// The warnings a file carries: its tables' (from the ledger) and its own.
function warningCountOf(file) {
  const tableWarnings = Object.values(file.units).reduce((total, entry) => total + entry.warnings, 0);
  return tableWarnings + file.verdicts.filter((verdict) => verdict.level === 'warn').length;
}

function SummaryCard({ label, count, icon, color, active, onClick }) {
  return (
    <Grid item xs={6} sm={4} md>
      <Paper
        variant="outlined"
        onClick={onClick}
        sx={{
          p: 2,
          cursor: 'pointer',
          textAlign: 'center',
          borderLeft: active ? `3px solid` : undefined,
          borderLeftColor: active ? `${color || 'primary'}.main` : undefined,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
          {icon}
          <Typography variant="h5" fontWeight={700}>{count}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Paper>
    </Grid>
  );
}

// `blockedLandingBatchId` is the batch whose landing the table's claim holds
// `blocked`, or null (the Data page listens to the claim): a file of that batch
// says "Landing needs attention — contact support" in place of its status.
function FilesHome({
  dataProfiles,
  companyId,
  tableId,
  table,
  onProfileSelect,
  profileStats,
  blockedLandingBatchId = null,
}) {
  const [activeFilters, setActiveFilters] = useState({
    profileId: null,
    hasWarnings: false,
    statuses: [],
    dateFrom: '',
    dateTo: '',
  });
  const [activeCard, setActiveCard] = useState(null); // tracks which summary card is active
  const [loadedFiles, setLoadedFiles] = useState([]);   // raw docs from the live listener
  const [queryMode, setQueryMode] = useState('scan');     // 'complete' | 'scan'
  const [exhausted, setExhausted] = useState(false);      // scan reached the oldest unit
  const [scannedCount, setScannedCount] = useState(0);    // raw docs read by the scan so far
  const [floorDate, setFloorDate] = useState(null);       // Date of the oldest scanned doc
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);                        // continuation fn, set by the effect
  const statsRef = useRef({ profileStats: {}, summaryStats: null });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // The file whose review dialog is open.
  const [reviewFileId, setReviewFileId] = useState(null);

  // { profileId: displayName } over the full profile list (active + deleted).
  const nameById = useMemo(() => buildProfileNameById(dataProfiles), [dataProfiles]);

  // Aggregate summary stats from profileStats (already flowing from DataCommandCenter)
  const summaryStats = useMemo(() => {
    const s = { total: 0, completed: 0, processing: 0, needsAttention: 0, withWarnings: 0 };
    Object.values(profileStats).forEach(ps => {
      s.total += ps.totalFiles || 0;
      s.completed += ps.completed || 0;
      s.processing += ps.processing || 0;
      s.needsAttention += ps.needsAttention || 0;
      s.withWarnings += ps.withWarnings || 0;
    });
    return s;
  }, [profileStats]);

  useEffect(() => {
    statsRef.current = { profileStats, summaryStats };
  }, [profileStats, summaryStats]);

  // Check if any filter is active
  const hasActiveFilters = activeFilters.profileId || activeFilters.hasWarnings || activeFilters.statuses.length > 0 || activeFilters.dateFrom || activeFilters.dateTo;

  useEffect(() => {
    if (!companyId || !tableId) return;
    let cancelled = false;
    let unsubscribe = null;

    setIsLoading(true);
    setError(null);
    setLoadedFiles([]);
    setQueryMode('scan');
    setExhausted(false);
    setScannedCount(0);
    setFloorDate(null);
    setIsLoadingMore(false);
    loadMoreRef.current = null;

    const collectionRef = collection(db, `users/${companyId}/tables/${tableId}/files`);
    const filters = activeFilters;

    const fail = (err) => {
      console.error('Error querying files:', err);
      if (cancelled) return;
      setError('Failed to load files');
      setIsLoading(false);
      setIsLoadingMore(false);
    };

    // ---- scan mode: created_at-desc spine, chunked getDocs, live range window --

    let floorSnap = null;        // DocumentSnapshot cursor: oldest doc read so far
    let scanExhausted = false;
    let scanned = 0;

    const attachScanListener = () => {
      if (unsubscribe) unsubscribe();
      let liveQ;
      if (floorSnap) {
        // The live window is range-bounded (>= floor), not count-bounded: every
        // loaded row stays live, and a new upload lands at the top without
        // pushing the oldest row out the way a limit() window would.
        liveQ = query(
          collectionRef,
          where('created_at', '>=', floorSnap.get('created_at')),
          orderBy('created_at', 'desc'),
        );
      } else {
        // Scan found nothing (an empty collection). Watch for arrivals; the
        // limit is a safety valve over a currently-empty set.
        liveQ = query(
          collectionRef,
          orderBy('created_at', 'desc'),
          limit(PAGE_SIZE),
        );
      }
      unsubscribe = onSnapshot(liveQ, { includeMetadataChanges: true }, (snap) => {
        if (cancelled) return;
        if (!floorSnap) {
          // An empty first scan has no cursor. Its first server-confirmed
          // arrivals establish the normal live range and reopen older paging
          // when the capped snapshot may have omitted documents.
          if (snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
          if (snap.size > 0) {
            floorSnap = snap.docs[snap.docs.length - 1];
            scanned = snap.size;
            scanExhausted = snap.size < PAGE_SIZE;
            applyFillResults();
          }
        }
        setLoadedFiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setIsLoading(false);
      }, fail);
    };

    const scanFill = async () => {
      let newMatches = 0;
      for (let i = 0; i < SCAN_CHUNKS_PER_FILL; i++) {
        if (scanExhausted || newMatches >= FILL_TARGET || cancelled) break;
        const chunkQ = query(
          collectionRef,
          orderBy('created_at', 'desc'),
          ...(floorSnap ? [startAfter(floorSnap)] : []),
          limit(PAGE_SIZE),
        );
        // A short cached page cannot prove that the server history ends here.
        const snap = await getDocsFromServer(chunkQ);
        if (cancelled) return;
        scanned += snap.size;
        if (snap.size > 0) floorSnap = snap.docs[snap.docs.length - 1];
        if (snap.size < PAGE_SIZE) scanExhausted = true;
        newMatches += snap.docs
          .filter(d => matchesFilters({ id: d.id, ...d.data() }, filters)).length;
      }
    };

    const applyFillResults = () => {
      setScannedCount(scanned);
      setExhausted(scanExhausted);
      setFloorDate(floorSnap ? safeToDate(floorSnap.get('created_at')) : null);
      attachScanListener();
      loadMoreRef.current = scanExhausted ? null : loadMore;
    };

    const loadMore = async () => {
      if (scanExhausted || cancelled) return;
      setIsLoadingMore(true);
      try {
        await scanFill();
      } catch (err) { fail(err); return; }
      if (cancelled) return;
      applyFillResults();
      setIsLoadingMore(false);
    };

    const startScanMode = async () => {
      setQueryMode('scan');
      try {
        await scanFill();
      } catch (err) { fail(err); return; }
      if (cancelled) return;
      applyFillResults();
    };

    // ---- complete mode: one bounded fetch of the full match set ---------------

    const startCompleteMode = (constraint) => {
      setQueryMode('complete');
      const q = query(collectionRef, constraint, limit(COMPLETE_FETCH_CAP + 1));
      unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, (snap) => {
        if (cancelled || snap.metadata.fromCache || snap.metadata.hasPendingWrites) return;
        if (snap.size > COMPLETE_FETCH_CAP) {
          // The file_stats counter undercounted: this set is NOT complete, and
          // without an orderBy it is an arbitrary __name__-ordered subset. Fall
          // back to the scan path instead of displaying it.
          unsubscribe();
          unsubscribe = null;
          startScanMode();
          return;
        }
        setLoadedFiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setExhausted(true);
        setScannedCount(snap.size);
        setIsLoading(false);
      }, fail);
    };

    // ---- mode selection --------------------------------------------------------

    const { profileStats: pStats, summaryStats: sStats } = statsRef.current;
    const candidates = [];
    if (filters.profileId) {
      candidates.push({
        key: 'profileId',
        count: pStats[filters.profileId]?.totalFiles || 0,
        constraint: where('data_profile_id', '==', filters.profileId),
      });
    }
    if (filters.hasWarnings) {
      candidates.push({
        key: 'hasWarnings',
        count: sStats?.withWarnings || 0,
        constraint: where('has_warnings', '==', true),
      });
    }
    if (filters.statuses.length > 0) {
      let est = 0;
      const statusSet = new Set(filters.statuses);
      if (STATUS_GROUPS.completed.some(s => statusSet.has(s))) est += sStats?.completed || 0;
      if (STATUS_GROUPS.processing.some(s => statusSet.has(s))) est += sStats?.processing || 0;
      if (STATUS_GROUPS.needsReview.some(s => statusSet.has(s))) est += sStats?.needsAttention || 0;
      candidates.push({
        key: 'statuses',
        count: est,
        constraint: where('status', 'in', filters.statuses),
      });
    }
    candidates.sort((a, b) => a.count - b.count);
    const primary = candidates[0] || null;

    if (primary && primary.count <= COMPLETE_FETCH_CAP) {
      startCompleteMode(primary.constraint);
    } else {
      startScanMode();
    }

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      loadMoreRef.current = null;
    };
  }, [companyId, tableId, activeFilters]);

  const visibleFiles = useMemo(() => loadedFiles
    .filter(file => matchesFilters(file, activeFilters))
    .sort(compareFiles), [loadedFiles, activeFilters]);

  // Summary card click handlers — toggle the corresponding filter
  const handleCardClick = (card) => {
    if (activeCard === card) {
      // Clicking same card again — clear it
      setActiveCard(null);
      setActiveFilters({ profileId: null, hasWarnings: false, statuses: [], dateFrom: '', dateTo: '' });
      return;
    }
    setActiveCard(card);
    switch (card) {
      case 'completed':
        setActiveFilters(prev => ({ ...prev, hasWarnings: false, statuses: STATUS_GROUPS.completed }));
        break;
      case 'processing':
        setActiveFilters(prev => ({ ...prev, hasWarnings: false, statuses: STATUS_GROUPS.processing }));
        break;
      case 'needsReview':
        setActiveFilters(prev => ({ ...prev, hasWarnings: false, statuses: STATUS_GROUPS.needsReview }));
        break;
      case 'warnings':
        setActiveFilters(prev => ({ ...prev, hasWarnings: true, statuses: [] }));
        break;
      default:
        setActiveFilters({ profileId: null, hasWarnings: false, statuses: [], dateFrom: '', dateTo: '' });
    }
  };

  const handleClearFilters = () => {
    setActiveFilters({ profileId: null, hasWarnings: false, statuses: [], dateFrom: '', dateTo: '' });
    setActiveCard(null);
  };

  const handleProfileFilterChange = (_, value) => {
    setActiveFilters(prev => ({ ...prev, profileId: value?.id || null }));
    setActiveCard(null);
  };

  const handleStatusFilterChange = (e) => {
    setActiveFilters(prev => ({ ...prev, statuses: e.target.value }));
    setActiveCard(null);
  };

  const handleWarningsToggle = (e) => {
    setActiveFilters(prev => ({ ...prev, hasWarnings: e.target.checked }));
    setActiveCard(null);
  };


  const handleProfileClick = (e, profileId) => {
    e.stopPropagation();
    const profile = dataProfiles.find(p => p.id === profileId);
    if (profile) onProfileSelect(profile);
  };

  // FilesHome grows with its content and the page around it scrolls. Each of the two
  // long lists, the upload cards and the files table, is capped with its own scroll, so
  // neither can squeeze the other off the page.
  return (
    <Box>
      {/* One upload list per table: picking another table starts a new list, so the old
          table's approved files and ticks are never shown under the new table. */}
      <SmartUploadFiles
        key={tableId}
        dataProfiles={dataProfiles}
        companyId={companyId}
        tableId={tableId}
        table={table}
      />

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Files</Typography>

        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <SummaryCard
            label="Total Files"
            count={summaryStats.total}
            active={activeCard === 'total'}
            onClick={() => handleCardClick('total')}
          />
          <SummaryCard
            label="Completed"
            count={summaryStats.completed}
            icon={<CheckCircleIcon color="success" sx={{ fontSize: 18 }} />}
            color="success"
            active={activeCard === 'completed'}
            onClick={() => handleCardClick('completed')}
          />
          <SummaryCard
            label="Processing"
            count={summaryStats.processing}
            icon={<HourglassEmptyIcon color="info" sx={{ fontSize: 18 }} />}
            color="info"
            active={activeCard === 'processing'}
            onClick={() => handleCardClick('processing')}
          />
          <SummaryCard
            label="Needs Review"
            count={summaryStats.needsAttention}
            icon={<ErrorIcon color="warning" sx={{ fontSize: 18 }} />}
            color="warning"
            active={activeCard === 'needsReview'}
            onClick={() => handleCardClick('needsReview')}
          />
          <SummaryCard
            label="Warnings"
            count={summaryStats.withWarnings}
            icon={<WarningIcon color="warning" sx={{ fontSize: 18 }} />}
            color="warning"
            active={activeCard === 'warnings'}
            onClick={() => handleCardClick('warnings')}
          />
        </Grid>

        {/* Filter Bar */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 2 }}>
          <Autocomplete
            size="small"
            options={dataProfiles}
            getOptionLabel={(opt) => isDeletedProfile(opt) ? `${profileName(opt)} (deleted)` : profileName(opt)}
            value={dataProfiles.find(p => p.id === activeFilters.profileId) || null}
            onChange={handleProfileFilterChange}
            renderInput={(params) => <TextField {...params} label="Profile" />}
            sx={{ minWidth: 200, '& .MuiInputBase-root': { height: 40 } }}
          />
          <FormControl size="small" sx={{ minWidth: 200, '& .MuiInputBase-root': { height: 40 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={activeFilters.statuses}
              onChange={handleStatusFilterChange}
              label="Status"
              renderValue={(selected) => selected.map(statusOptionLabel).join(', ')}
            >
              {ALL_STATUSES.map(s => (
                <MenuItem key={s} value={s}>{statusOptionLabel(s)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Wide enough for DD/MM/YYYY to sit alongside the calendar and clear
              buttons without the text being clipped. */}
          <AppDatePicker
            label="From"
            value={activeFilters.dateFrom}
            onChange={(nextDate) => { setActiveFilters(prev => ({ ...prev, dateFrom: nextDate })); setActiveCard(null); }}
            slotProps={{ textField: { sx: { width: 180, '& .MuiInputBase-root': { height: 40 } } } }}
          />
          <AppDatePicker
            label="To"
            value={activeFilters.dateTo}
            onChange={(nextDate) => { setActiveFilters(prev => ({ ...prev, dateTo: nextDate })); setActiveCard(null); }}
            slotProps={{ textField: { sx: { width: 180, '& .MuiInputBase-root': { height: 40 } } } }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={activeFilters.hasWarnings}
                onChange={handleWarningsToggle}
              />
            }
            label="Warnings Only"
          />
          {hasActiveFilters && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={handleClearFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>

        {/* Active Filter Chips */}
        {hasActiveFilters && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {activeFilters.profileId && (
              <Chip
                label={`Profile: ${nameById[activeFilters.profileId] ?? activeFilters.profileId}`}
                size="small"
                onDelete={() => setActiveFilters(prev => ({ ...prev, profileId: null }))}
              />
            )}
            {activeFilters.statuses.map(s => (
              <Chip
                key={s}
                label={`Status: ${statusOptionLabel(s)}`}
                size="small"
                onDelete={() => setActiveFilters(prev => ({
                  ...prev,
                  statuses: prev.statuses.filter(st => st !== s),
                }))}
              />
            ))}
            {activeFilters.dateFrom && (
              <Chip
                label={`From: ${format(parseLocalDate(activeFilters.dateFrom), 'dd/MM/yyyy')}`}
                size="small"
                onDelete={() => setActiveFilters(prev => ({ ...prev, dateFrom: '' }))}
              />
            )}
            {activeFilters.dateTo && (
              <Chip
                label={`To: ${format(parseLocalDate(activeFilters.dateTo), 'dd/MM/yyyy')}`}
                size="small"
                onDelete={() => setActiveFilters(prev => ({ ...prev, dateTo: '' }))}
              />
            )}
            {activeFilters.hasWarnings && (
              <Chip
                label="Warnings Only"
                size="small"
                color="warning"
                onDelete={() => setActiveFilters(prev => ({ ...prev, hasWarnings: false }))}
              />
            )}
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Results */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleFiles.length === 0 ? (
          queryMode === 'scan' && !exhausted ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No matches in the {scannedCount} most recent uploads
                {floorDate ? ` (searched back to ${formatDateOnly(floorDate)})` : ''}.
              </Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={isLoadingMore}
                startIcon={isLoadingMore ? <CircularProgress size={14} /> : null}
                onClick={() => loadMoreRef.current?.()}
              >
                {isLoadingMore ? 'Searching…' : 'Search older files'}
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {hasActiveFilters ? 'No files match your filters.' : 'No files found.'}
            </Typography>
          )
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {queryMode === 'complete'
                ? `Showing all ${visibleFiles.length} matching file${visibleFiles.length !== 1 ? 's' : ''}`
                : exhausted
                  ? `Showing ${visibleFiles.length} file${visibleFiles.length !== 1 ? 's' : ''} — searched the full file history`
                  : `Showing ${visibleFiles.length} file${visibleFiles.length !== 1 ? 's' : ''} — searched the ${scannedCount} most recent uploads${floorDate ? ` (back to ${format(floorDate, 'dd MMM yyyy')})` : ''}`}
            </Typography>
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Profile</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Tables</TableCell>
                    <TableCell>Warnings</TableCell>
                    <TableCell>Reporting Date</TableCell>
                    <TableCell>Upload Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleFiles.map((file) => {
                    const createdAt = safeToDate(file.created_at);
                    const warningCount = warningCountOf(file);
                    const landingBlocked = blockedLandingBatchId !== null && file.batch_id === blockedLandingBatchId;
                    return (
                      <TableRow
                        key={file.id}
                        hover
                        onClick={() => setReviewFileId(file.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          {dataProfiles.some(p => p.id === file.data_profile_id) ? (
                            <Link
                              component="button"
                              variant="body2"
                              underline="hover"
                              onClick={(e) => handleProfileClick(e, file.data_profile_id)}
                            >
                              {nameById[file.data_profile_id]}
                            </Link>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {file.data_profile_id || '—'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>{file.name}</Typography>
                        </TableCell>
                        <TableCell>
                          {landingBlocked ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ErrorIcon color="error" sx={{ fontSize: 18 }} />
                              <Typography variant="body2" color="error">{LANDING_ATTENTION_MESSAGE}</Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FileStatusIcon status={file.status} />
                              <Typography variant="body2">{fileStatusLabel(file.status)}</Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{ledgerSummaryLine(file)}</Typography>
                        </TableCell>
                        <TableCell>
                          {warningCount > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <WarningIcon color="warning" sx={{ fontSize: 16 }} />
                              <Typography variant="body2" color="warning.main">
                                {warningCount}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{file.date ? formatDateOnly(file.date) : '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {createdAt ? formatTimestamp(createdAt) : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {queryMode === 'scan' && !exhausted && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={isLoadingMore}
                    startIcon={isLoadingMore ? <CircularProgress size={14} /> : null}
                    onClick={() => loadMoreRef.current?.()}
                  >
                    {isLoadingMore ? 'Searching…' : 'Search older files'}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>

      {reviewFileId && table && (
        <FileReviewDialog
          key={reviewFileId}
          open
          companyId={companyId}
          tableId={tableId}
          table={table}
          fileId={reviewFileId}
          onClose={() => setReviewFileId(null)}
          onNotice={setError}
        />
      )}
    </Box>
  );
}

export default FilesHome;
